import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  ItemDetail,
  StockItemRecord,
  StockMovementRecord,
  SupplierRecord,
  WarehouseRecord,
} from './erp.types';

const WH = '/users/v1/warehouse';
const PROC = '/users/v1/procurement';
const KEY = ['erp'] as const;

/** A refresh somebody asked for on the Warehouse screen: refetches every ACTIVE ERP read (items,
 *  movements, warehouses, suppliers) — nothing here polls, and stock is moved by receiving,
 *  cultivation and other operators while the screen is open. Resolves when they have all landed. */
export function useRefreshWarehouse() {
  const qc = useQueryClient();
  return () => qc.refetchQueries({ queryKey: KEY, type: 'active' });
}

export function useWarehouses(siteId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'warehouses', siteId],
    queryFn: () => api.get<WarehouseRecord[]>(`${WH}/warehouses?site_id=${siteId}`),
    enabled: !!siteId,
  });
}

export function useUpsertWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: Partial<WarehouseRecord>) => api.post<WarehouseRecord>(`${WH}/warehouses`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useStockItems() {
  return useQuery({ queryKey: [...KEY, 'items'], queryFn: () => api.get<StockItemRecord[]>(`${WH}/items`) });
}

export function useStockItemDetail(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'item', id],
    queryFn: () => api.get<ItemDetail>(`${WH}/items/${id}`),
    enabled: !!id,
  });
}

export function useUpsertStockItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: Partial<StockItemRecord>) => api.post<StockItemRecord>(`${WH}/items`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteStockItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${WH}/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useStockMovements(itemId?: string) {
  return useQuery({
    queryKey: [...KEY, 'movements', itemId ?? 'all'],
    queryFn: () => api.get<StockMovementRecord[]>(`${WH}/movements${itemId ? `?item_id=${itemId}` : ''}`),
  });
}

export function usePostReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: {
      item_id: string;
      warehouse_id: string;
      quantity: number;
      lot_code?: string;
      expiry_date?: string;
      note?: string;
    }) => api.post<string>(`${WH}/receipts`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePostConsumption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: { item_id: string; quantity: number; note?: string }) =>
      api.post<boolean>(`${WH}/consumptions`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePostStocktake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: { item_id: string; warehouse_id: string; counted: number; note?: string }) =>
      api.post<number>(`${WH}/stocktakes`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSuppliers() {
  return useQuery({
    queryKey: [...KEY, 'suppliers'],
    queryFn: () => api.get<SupplierRecord[]>(`${PROC}/suppliers`),
  });
}

export function useUpsertSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: Partial<SupplierRecord>) => api.post<SupplierRecord>(`${PROC}/suppliers`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// R-234: the requisition/order hooks that lived here are GONE with the cloud
// PR/PO chain they called (`/users/v1/procurement/{requisitions,orders}`).
// Procurement is site-local now — see `domains/procurement`.
//
// The SUPPLIER hooks above deliberately stay: `/users/v1/procurement/suppliers`
// survived the demolition because the supplier master is not part of the buying
// chain, and the Warehouse page still reads it.
